import { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/helpers';
import { api } from '../../utils/api';
import { getWeekRange, SubmissionCell } from '../../utils/homeworkFeedback';

const subjectPill = [
  'bg-blue-100 text-blue-700', 'bg-green-100 text-green-700', 'bg-purple-100 text-purple-700',
  'bg-orange-100 text-orange-700', 'bg-pink-100 text-pink-700', 'bg-teal-100 text-teal-700',
  'bg-yellow-100 text-yellow-800', 'bg-red-100 text-red-700',
];

// Weekly, per-subject homework report for a parent/student's own child —
// same matrix layout as the admin Reports page's "Student Wise" view, but
// scoped to the current week and to whichever child is being viewed, with
// no class/student pickers since there's nothing to pick.
export default function WeeklyReport() {
  const { students, classes, subjects, homework } = useApp();
  const { currentUser } = useAuth();
  const today = new Date().toISOString().split('T')[0];

  const isStudent = currentUser?.role === 'student';
  const isParent  = ['parent', 'father', 'mother'].includes(currentUser?.role);
  const profId    = currentUser?.profileId;

  const myChildren = isParent ? students.filter(s => (s.parentId === profId || s.id === profId)) : [];
  const studentRecord = isStudent ? students.find(s => s.id === profId) : null;

  const [selectedChildId, setSelectedChildId] = useState(myChildren[0]?.id || null);
  const child = isStudent ? studentRecord : (myChildren.find(c => c.id === selectedChildId) || myChildren[0]);
  const cls = classes.find(c => c.id === child?.classId);

  const [weekOffset, setWeekOffset] = useState(0);
  const week = getWeekRange(weekOffset);

  const [submissionsMap, setSubmissionsMap] = useState({}); // homeworkId -> feedback row

  useEffect(() => {
    if (!child?.id) { setSubmissionsMap({}); return; }
    let cancelled = false;
    api.get(`/students/${child.id}/homework-submissions`).then(rows => {
      if (cancelled) return;
      const map = {};
      (Array.isArray(rows) ? rows : []).forEach(r => { if (r.homeworkId) map[r.homeworkId] = r; });
      setSubmissionsMap(map);
    }).catch(() => { if (!cancelled) setSubmissionsMap({}); });
    return () => { cancelled = true; };
  }, [child?.id]);

  const hwList = useMemo(() => {
    if (!child?.classId) return [];
    return homework.filter(h => h.classId === child.classId && h.dueDate >= week.start && h.dueDate <= week.end);
  }, [homework, child?.classId, week.start, week.end]);

  const dates = [...new Set(hwList.map(h => h.dueDate || 'Unknown'))].sort();
  const subjectIds = [...new Set(hwList.map(h => h.subjectId))];
  const subjectList = subjectIds.map(id => subjects.find(s => s.id === id)).filter(Boolean);

  const lookup = {};
  hwList.forEach(hw => {
    const d = hw.dueDate || 'Unknown';
    if (!lookup[d]) lookup[d] = {};
    lookup[d][hw.subjectId] = hw;
  });

  const doneCount = hwList.filter(h => submissionsMap[h.id]?.status === 'completed').length;
  const pct = hwList.length > 0 ? Math.round((doneCount / hwList.length) * 100) : 0;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Weekly Homework Report</h1>
        {child && <p className="text-gray-500 text-sm">{child.firstName} {child.lastName} · Class {cls?.name} – Section {cls?.section}</p>}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 mb-5 flex flex-wrap items-center gap-4">
        {isParent && myChildren.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            {myChildren.map(c => (
              <button key={c.id} onClick={() => setSelectedChildId(c.id)}
                className={`px-3 py-1 rounded-lg text-sm font-medium border transition-colors ${
                  c.id === child?.id ? 'text-white border-blue-900' : 'bg-white border-gray-300 hover:bg-gray-50'
                }`}
                style={c.id === child?.id ? { backgroundColor: '#1e3a8a' } : {}}>
                {c.firstName} {c.lastName}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3">
          <button onClick={() => setWeekOffset(w => w - 1)} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"><ChevronLeft size={16} /></button>
          <span className="font-semibold min-w-[200px] text-center text-sm">
            {weekOffset === 0 ? 'This Week' : weekOffset === -1 ? 'Last Week' : weekOffset === 1 ? 'Next Week' : week.label}
            <span className="block font-normal">{week.label}</span>
          </span>
          <button onClick={() => setWeekOffset(w => w + 1)} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"><ChevronRight size={16} /></button>
        </div>
      </div>

      {!child ? (
        <div className="bg-white rounded-xl p-12 text-center">No student record found</div>
      ) : hwList.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">No homework found for this week</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between flex-wrap gap-3">
            <div className="flex gap-2">
              <span className="bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">{doneCount} Done</span>
              <span className="bg-red-100 text-red-600 px-2.5 py-1 rounded-full font-medium">{hwList.length - doneCount} Pending</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-24 bg-gray-200 rounded-full h-2">
                <div className="h-2 rounded-full bg-green-500" style={{ width: `${pct}%` }} />
              </div>
              <span className="font-bold">{pct}%</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="text-sm border-collapse" style={{ minWidth: `${160 + dates.length * 100}px` }}>
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200">
                  <th className="sticky left-0 bg-gray-100 px-5 py-2.5 text-left font-semibold border-r border-gray-200 min-w-[140px]">Subject</th>
                  {dates.map(date => {
                    const isToday = date === today;
                    const isPast  = date < today;
                    return (
                      <th key={date} className={`px-3 py-1 text-center border-r border-gray-200 min-w-[100px] ${isToday ? 'bg-orange-50' : isPast ? 'bg-gray-100' : 'bg-green-50'}`}>
                        <span className={`block text-[11px] font-bold ${isToday ? 'text-orange-600' : isPast ? 'text-gray-500' : 'text-green-700'}`}>
                          {isToday ? 'Today' : formatDate(date)}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {subjectList.map((sub, sIdx) => (
                  <tr key={sub.id} className={sIdx % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/40 hover:bg-gray-100'}>
                    <td className={`sticky left-0 px-5 py-3 border-r border-gray-200 ${sIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <span className={`inline-block font-bold px-2.5 py-1 rounded-full ${subjectPill[sIdx % subjectPill.length]}`}>
                        {sub.name}
                      </span>
                    </td>
                    {dates.map(date => {
                      const hw = lookup[date]?.[sub.id];
                      if (!hw) {
                        return (
                          <td key={date} className="px-3 py-3 text-center border-r border-gray-100">
                            <span className="text-gray-300 text-sm">—</span>
                          </td>
                        );
                      }
                      return (
                        <td key={date} className="px-2 py-2 text-center border-r border-gray-100">
                          <span className="block text-[10px] mb-1 truncate max-w-[80px] mx-auto" title={hw.title}>{hw.title}</span>
                          <SubmissionCell sub={submissionsMap[hw.id]} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
